// ABOUTME: Quantity configuration form — three sections (Quantity, Full Loads, Load Balance) with per-field inheritance.
// ABOUTME: react-hook-form + useFieldArray for the Full Loads options table; surfaces validation via onValidChange.
import {Button, Icon, TextMedium} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {mdiInformationOutline, mdiPlus, mdiTrashCanOutline} from '@mdi/js'
import React, {useEffect, useMemo} from 'react'
import {Path, PathValue, useFieldArray, useForm, useWatch} from 'react-hook-form'

import {useTranslation} from '../../../../hooks/useTranslation'
import {QuantityEntityOptions} from '../types'
import {
  QuantityConfig,
  QuantityFormValues,
  QuantityOverrides,
  QuantityValues,
} from '../types/quantity'
import {uomRefsToOptions, withFallback} from '../utils/entityCatalogOptions'
import {FilterDropdown} from './FilterDropdown'
import {NumericalInput} from './NumericalInput'
import {OverrideDot} from './OverrideDot'
import {SectionTitle} from './SectionTitle'
import {ToggleSwitch} from './ToggleSwitch'

export interface QuantityFormProps {
  config: QuantityConfig
  isReadOnly: boolean
  isCountryDefault: boolean
  onDirtyChange: (dirty: boolean) => void
  onValidChange: (valid: boolean) => void
  onValuesChange?: (values: QuantityFormValues) => void
  /** Backend-provided options (UoM list + load option availability). Falls back to saved values. */
  options?: QuantityEntityOptions
}

export interface QuantityOptionRowErrors {
  numberOfTrucks?: string
  minQuantity?: string
  maxQuantity?: string
}

export interface QuantityErrors {
  uomId?: string
  fullLoadsUomId?: string
  step?: string
  max?: string
  numberOfDecimals?: string
  maxEligibleQuantity?: string
  defaultNumberOfTrucks?: string
  optionRows: Record<number, QuantityOptionRowErrors>
}

const ERR = {
  uomRequired: 'countryConfigurations.quantity.errors.uomRequired',
  stepPositive: 'countryConfigurations.quantity.errors.stepPositive',
  maxBelowMin: 'countryConfigurations.quantity.errors.maxBelowMin',
  decimalsNonNegative: 'countryConfigurations.quantity.errors.decimalsNonNegative',
  maxEligibleBelowMin: 'countryConfigurations.quantity.errors.maxEligibleBelowMin',
  defaultTruckNotInOptions: 'countryConfigurations.quantity.errors.defaultTruckNotInOptions',
  optionMaxBelowMin: 'countryConfigurations.quantity.errors.optionMaxBelowMin',
  optionTrucksUnique: 'countryConfigurations.quantity.errors.optionTrucksUnique',
}

/** Pure validation of all sections. Exported for unit testing. */
export const computeQuantityErrors = (values: QuantityFormValues): QuantityErrors => {
  const errors: QuantityErrors = {optionRows: {}}

  if (!values.uomId) errors.uomId = ERR.uomRequired
  if (values.fullLoadsEnabled && !values.fullLoadsUomId) errors.fullLoadsUomId = ERR.uomRequired
  if (values.step <= 0) errors.step = ERR.stepPositive
  if (values.max < values.min) errors.max = ERR.maxBelowMin
  if (values.numberOfDecimals < 0) errors.numberOfDecimals = ERR.decimalsNonNegative

  if (values.loadBalanceEnabled && values.maxEligibleQuantity <= 0) {
    errors.maxEligibleQuantity = ERR.stepPositive
  } else if (values.loadBalanceEnabled && values.maxEligibleQuantity < values.minEligibleQuantity) {
    errors.maxEligibleQuantity = ERR.maxEligibleBelowMin
  }

  if (values.fullLoadsEnabled) {
    const options = values.fullLoadOptions.options
    const seenTrucks = new Set<number>()
    options.forEach((opt, index) => {
      const rowErrors: QuantityOptionRowErrors = {}
      if (opt.maxQuantity < opt.minQuantity) rowErrors.maxQuantity = ERR.optionMaxBelowMin
      if (opt.numberOfTrucks <= 0) {
        rowErrors.numberOfTrucks = ERR.stepPositive
      } else if (seenTrucks.has(opt.numberOfTrucks)) {
        rowErrors.numberOfTrucks = ERR.optionTrucksUnique
      } else {
        seenTrucks.add(opt.numberOfTrucks)
      }
      if (Object.keys(rowErrors).length > 0) errors.optionRows[index] = rowErrors
    })

    const truckNumbers = options.map((o) => o.numberOfTrucks)
    if (!truckNumbers.includes(values.fullLoadOptions.defaultNumberOfTrucks)) {
      errors.defaultNumberOfTrucks = ERR.defaultTruckNotInOptions
    }
  }

  return errors
}

/** True when any error (top-level or per-row) is present. */
export const hasQuantityErrors = (errors: QuantityErrors): boolean =>
  Boolean(
    errors.uomId ||
      errors.fullLoadsUomId ||
      errors.step ||
      errors.max ||
      errors.numberOfDecimals ||
      errors.maxEligibleQuantity ||
      errors.defaultNumberOfTrucks
  ) || Object.keys(errors.optionRows).length > 0

/**
 * Build the "default number of trucks" dropdown options from the Full Loads rows: only real truck
 * counts (> 0, so freshly-added empty rows are excluded), de-duplicated and sorted ascending.
 * Exported for unit testing.
 */
export const buildDefaultTruckOptions = (
  options: QuantityFormValues['fullLoadOptions']['options'],
): {value: string; label: string}[] => {
  const unique = Array.from(
    new Set(options.map((o) => o.numberOfTrucks).filter((n) => n > 0)),
  ).sort((a, b) => a - b)
  return unique.map((n) => ({value: String(n), label: String(n)}))
}

const toFormValues = (cfg: QuantityConfig): QuantityFormValues => ({
  uom: cfg.uom,
  uomId: cfg.uomId,
  default: cfg.default,
  min: cfg.min,
  max: cfg.max,
  maxOrderConfirmable: cfg.maxOrderConfirmable,
  step: cfg.step,
  shortLoadThreshold: cfg.shortLoadThreshold,
  numberOfDecimals: cfg.numberOfDecimals,
  roundToChangeIncrement: cfg.roundToChangeIncrement,
  fullLoadsEnabled: cfg.fullLoadsEnabled,
  fullLoadsUom: cfg.fullLoadsUom,
  fullLoadsUomId: cfg.fullLoadsUomId,
  fullLoadOptions: cfg.fullLoadOptions,
  loadBalanceEnabled: cfg.loadBalanceEnabled,
  minEligibleQuantity: cfg.minEligibleQuantity,
  maxEligibleQuantity: cfg.maxEligibleQuantity,
})

type NumericQuantityField = keyof Pick<
  QuantityValues,
  | 'default'
  | 'min'
  | 'max'
  | 'maxOrderConfirmable'
  | 'step'
  | 'shortLoadThreshold'
  | 'numberOfDecimals'
>

const QUANTITY_FIELDS: {key: NumericQuantityField; labelKey: string; tooltipKey?: string; integer?: boolean}[] = [
  {key: 'default', labelKey: 'countryConfigurations.quantity.fields.default'},
  {key: 'min', labelKey: 'countryConfigurations.quantity.fields.min'},
  {key: 'max', labelKey: 'countryConfigurations.quantity.fields.max'},
  {
    key: 'maxOrderConfirmable',
    labelKey: 'countryConfigurations.quantity.fields.maxOrderConfirmable',
    tooltipKey: 'countryConfigurations.quantity.fields.maxOrderConfirmableTooltip',
  },
  {key: 'step', labelKey: 'countryConfigurations.quantity.fields.step'},
  {
    key: 'shortLoadThreshold',
    labelKey: 'countryConfigurations.quantity.fields.shortLoadThreshold',
    tooltipKey: 'countryConfigurations.quantity.fields.shortLoadThresholdTooltip',
  },
  {
    key: 'numberOfDecimals',
    labelKey: 'countryConfigurations.quantity.fields.numberOfDecimals',
    tooltipKey: 'countryConfigurations.quantity.fields.numberOfDecimalsTooltip',
    integer: true,
  },
]

/**
 * Shared two-column field layout: a fixed-width label track + a control track. Using a fixed label
 * track (instead of a content-sized cell) keeps every control aligned regardless of label length.
 */
const FIELD_ROW = 'grid grid-cols-[15rem_minmax(0,1fr)] gap-x-6 items-start w-full'
const FIELD_LABEL = 'flex flex-col justify-center min-h-10 min-w-0'
/** Control wrapper width — keeps numeric steppers and dropdowns aligned to the same column. */
const FIELD_CONTROL = 'w-[228px] shrink-0'

export const QuantityForm: React.FC<QuantityFormProps> = ({
  config,
  isReadOnly,
  isCountryDefault,
  onDirtyChange,
  onValidChange,
  onValuesChange,
  options,
}) => {
  const {t} = useTranslation()
  const showDot = !isCountryDefault
  const formDefaultValues = useMemo(() => toFormValues(config), [config])
  const {control, setValue, reset, formState: {isDirty}} = useForm<QuantityFormValues>({
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  const {fields, append, remove} = useFieldArray({control, name: 'fullLoadOptions.options'})

  const watched = useWatch({control}) as QuantityFormValues
  const values: QuantityFormValues = useMemo(() => ({...formDefaultValues, ...watched}), [
    formDefaultValues,
    watched,
  ])

  const errors = useMemo(() => computeQuantityErrors(values), [values])
  const isValid = !hasQuantityErrors(errors)

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    onValidChange(isValid)
  }, [isValid, onValidChange])

  useEffect(() => {
    if (isDirty) onValuesChange?.(values)
  }, [values, isDirty, onValuesChange])

  const setField = (field: Path<QuantityFormValues>) => (val: number | string | boolean) => {
    setValue(field, val as PathValue<QuantityFormValues, Path<QuantityFormValues>>, {
      shouldDirty: true,
    })
  }

  /** Shared label cell: inheritance dot (when not country default and field is overridden) + optional ⓘ tooltip. */
  const renderLabel = (
    labelKey: string,
    overrideKey: keyof QuantityOverrides,
    tooltipKey?: string,
  ) => (
    <div className="flex gap-0.5 items-center whitespace-nowrap">
      {showDot && config.overrides[overrideKey] && <OverrideDot />}
      <TextMedium className="text-foreground line-clamp-1">{t(labelKey)}</TextMedium>
      {tooltipKey && (
        <CountryConfigsTooltip content={t(tooltipKey)} delayMs={300}>
          <span className="text-foreground flex items-center cursor-default">
            <Icon path={mdiInformationOutline} size="regular" />
          </span>
        </CountryConfigsTooltip>
      )}
    </div>
  )

  // UoM options: prefer the backend catalog list; fall back to the single saved value so the
  // dropdown always shows at least the configured UoM when the backend group is absent.
  const backendUomOptions = useMemo(
    () => uomRefsToOptions(options?.availableUnitsOfMeasure),
    [options]
  )
  const uomOptions = useMemo(
    () =>
      withFallback(
        backendUomOptions,
        values.uomId ? [{value: values.uomId, label: values.uom || values.uomId}] : [],
      ),
    [backendUomOptions, values.uomId, values.uom]
  )
  // N4: main UoM and full-loads UoM intentionally share the same backend list
  // (options.availableUnitsOfMeasure); the backend exposes a single UoM group for quantity.
  const fullLoadsUomOptions = useMemo(
    () =>
      withFallback(
        backendUomOptions,
        values.fullLoadsUomId
          ? [{value: values.fullLoadsUomId, label: values.fullLoadsUom || values.fullLoadsUomId}]
          : [],
      ),
    [backendUomOptions, values.fullLoadsUomId, values.fullLoadsUom]
  )

  // Section availability: backend gates Full Loads / Load Balance. When the group is absent
  // (undefined), keep the current behaviour and render both sections.
  const showFullLoads = options?.loadOptions?.fullLoadsAvailable !== false
  const showLoadBalance = options?.loadOptions?.loadBalanceAvailable !== false

  const defaultTruckOptions = useMemo(
    () => buildDefaultTruckOptions(values.fullLoadOptions.options),
    [values.fullLoadOptions.options]
  )

  const renderNumericRow = (
    labelKey: string,
    fieldValue: number,
    onChange: (v: number) => void,
    overrideKey: keyof QuantityOverrides,
    options?: {integer?: boolean; suffix?: string; error?: string; dataCy?: string; tooltipKey?: string},
  ) => (
    <div className={FIELD_ROW}>
      <div className={FIELD_LABEL}>
        {renderLabel(labelKey, overrideKey, options?.tooltipKey)}
      </div>
      <div className={FIELD_CONTROL}>
        <NumericalInput
          value={fieldValue}
          onChange={onChange}
          min={0}
          step={options?.integer ? 1 : undefined}
          suffix={options?.suffix}
          disabled={isReadOnly}
          error={options?.error ? t(options.error) : undefined}
          aria-label={t(labelKey)}
          data-cy={options?.dataCy}
        />
      </div>
    </div>
  )

  return (
    <div className="flex flex-col gap-8 p-6 w-full" data-cy="quantity-form">
      {/* --- Quantity section --- */}
      <section className="flex flex-col gap-6">
        <SectionTitle
          titleKey="countryConfigurations.quantity.sections.quantity.title"
          tooltipKey="countryConfigurations.quantity.sections.quantity.tooltip"
        />

        <div className={FIELD_ROW}>
          <div className={FIELD_LABEL}>
            {renderLabel('countryConfigurations.quantity.fields.uom', 'uom')}
          </div>
          <div className={FIELD_CONTROL}>
            <FilterDropdown
              value={values.uomId ?? ''}
              onChange={(v) => {
                const opt = uomOptions.find((o) => o.value === v)
                setField('uomId')(v)
                setField('uom')(opt?.label ?? '')
              }}
              options={uomOptions}
              label={t('countryConfigurations.quantity.fields.uom')}
              size="medium"
              disabled={isReadOnly}
              data-cy="quantity-uom"
            />
            {errors.uomId && <p role="alert" className="text-xs leading-4 text-negative px-3">{t(errors.uomId)}</p>}
          </div>
        </div>

        {QUANTITY_FIELDS.map((field) =>
          React.cloneElement(
            renderNumericRow(field.labelKey, values[field.key], setField(field.key), field.key, {
              integer: field.integer,
              suffix: field.key === 'numberOfDecimals' ? undefined : values.uom,
              tooltipKey: field.tooltipKey,
              error:
                field.key === 'step'
                  ? errors.step
                  : field.key === 'max'
                    ? errors.max
                    : field.key === 'numberOfDecimals'
                      ? errors.numberOfDecimals
                      : undefined,
              dataCy: `quantity-field-${field.key}`,
            }),
            {key: field.key},
          ),
        )}

        <div className="grid grid-cols-[15rem_minmax(0,1fr)] gap-x-6 items-center w-full">
          <div className={FIELD_LABEL}>
            {renderLabel(
              'countryConfigurations.quantity.fields.roundToChangeIncrement',
              'roundToChangeIncrement',
              'countryConfigurations.quantity.fields.roundToChangeIncrementTooltip',
            )}
          </div>
          <ToggleSwitch
            checked={values.roundToChangeIncrement}
            onChange={setField('roundToChangeIncrement')}
            disabled={isReadOnly}
            aria-label={t('countryConfigurations.quantity.fields.roundToChangeIncrement')}
            data-cy="quantity-round-to-increment"
          />
        </div>
      </section>

      <hr className="border-border" />

      {/* --- Full Loads section --- */}
      {showFullLoads && (
      <section className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <SectionTitle
            titleKey="countryConfigurations.quantity.sections.fullLoads.title"
            tooltipKey="countryConfigurations.quantity.sections.fullLoads.tooltip"
          />
          <div className="flex gap-0.5 items-center">
            {showDot && config.overrides.fullLoadsEnabled && <OverrideDot />}
            <CountryConfigsTooltip
              content={t('countryConfigurations.quantity.fields.fullLoadsEnabledTooltip')}
              delayMs={300}
            >
              <span className="text-foreground flex items-center cursor-default">
                <Icon path={mdiInformationOutline} size="regular" />
              </span>
            </CountryConfigsTooltip>
            <ToggleSwitch
              checked={values.fullLoadsEnabled}
              onChange={setField('fullLoadsEnabled')}
              disabled={isReadOnly}
              aria-label={t('countryConfigurations.quantity.sections.fullLoads.title')}
              data-cy="quantity-fullloads-enabled"
            />
          </div>
        </div>

        {values.fullLoadsEnabled && (
          <>
            <div className={FIELD_ROW}>
              <div className={FIELD_LABEL}>
                {renderLabel('countryConfigurations.quantity.fields.fullLoadsUom', 'fullLoadsUom')}
              </div>
              <div className={FIELD_CONTROL}>
                <FilterDropdown
                  value={values.fullLoadsUomId ?? ''}
                  onChange={(v) => {
                    const opt = fullLoadsUomOptions.find((o) => o.value === v)
                    setField('fullLoadsUomId')(v)
                    setField('fullLoadsUom')(opt?.label ?? '')
                  }}
                  options={fullLoadsUomOptions}
                  label={t('countryConfigurations.quantity.fields.fullLoadsUom')}
                  size="medium"
                  disabled={isReadOnly}
                  data-cy="quantity-fullloads-uom"
                />
                {errors.fullLoadsUomId && (
                  <p role="alert" className="text-xs leading-4 text-negative px-3">{t(errors.fullLoadsUomId)}</p>
                )}
              </div>
            </div>

            <table
              className="w-auto text-left border-separate border-spacing-0"
              data-cy="quantity-fullloads-options-table"
            >
              <thead>
                <tr className="text-xs text-foreground_faded">
                  <th className="py-2 pr-4 font-medium">
                    <span className="flex gap-0.5 items-center">
                      {showDot && config.overrides.fullLoadsOptions && <OverrideDot />}
                      {t('countryConfigurations.quantity.table.numberOfTrucks')}
                    </span>
                  </th>
                  <th className="py-2 pr-4 font-medium">
                    {t('countryConfigurations.quantity.table.minQuantity')}
                  </th>
                  <th className="py-2 pr-4 font-medium">
                    {t('countryConfigurations.quantity.table.maxQuantity')}
                  </th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {fields.map((row, index) => {
                  const rowValue = values.fullLoadOptions.options[index]
                  const rowErrors = errors.optionRows[index]
                  if (!rowValue) return null
                  return (
                    <tr key={row.id} data-cy={`quantity-option-row-${index}`}>
                      <td className="py-2 pr-4 align-top">
                        <NumericalInput
                          value={rowValue.numberOfTrucks}
                          onChange={setField(`fullLoadOptions.options.${index}.numberOfTrucks`)}
                          min={0}
                          step={1}
                          disabled={isReadOnly}
                          error={rowErrors?.numberOfTrucks ? t(rowErrors.numberOfTrucks) : undefined}
                          aria-label={`${t('countryConfigurations.quantity.table.numberOfTrucks')} ${index + 1}`}
                          data-cy={`quantity-option-trucks-${index}`}
                        />
                      </td>
                      <td className="py-2 pr-4 align-top">
                        <NumericalInput
                          value={rowValue.minQuantity}
                          onChange={setField(`fullLoadOptions.options.${index}.minQuantity`)}
                          min={0}
                          disabled={isReadOnly}
                          aria-label={`${t('countryConfigurations.quantity.table.minQuantity')} ${index + 1}`}
                          data-cy={`quantity-option-min-${index}`}
                        />
                      </td>
                      <td className="py-2 pr-4 align-top">
                        <NumericalInput
                          value={rowValue.maxQuantity}
                          onChange={setField(`fullLoadOptions.options.${index}.maxQuantity`)}
                          min={0}
                          disabled={isReadOnly}
                          error={rowErrors?.maxQuantity ? t(rowErrors.maxQuantity) : undefined}
                          aria-label={`${t('countryConfigurations.quantity.table.maxQuantity')} ${index + 1}`}
                          data-cy={`quantity-option-max-${index}`}
                        />
                      </td>
                      <td className="py-2 align-top">
                        <Button
                          variant="tertiary"
                          square
                          size="small"
                          type="button"
                          disabled={isReadOnly}
                          onClick={() => remove(index)}
                          aria-label={t('countryConfigurations.quantity.table.removeRow')}
                          data-cy={`quantity-option-remove-${index}`}
                        >
                          <Icon path={mdiTrashCanOutline} size="regular" />
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <div>
              <Button
                variant="secondary"
                size="small"
                type="button"
                icon={mdiPlus}
                disabled={isReadOnly}
                onClick={() => append({numberOfTrucks: 0, minQuantity: 0, maxQuantity: 0})}
                data-cy="quantity-option-add"
              >
                {t('countryConfigurations.quantity.table.addRow')}
              </Button>
            </div>

            <div className={FIELD_ROW}>
              <div className={FIELD_LABEL}>
                <TextMedium className="text-foreground line-clamp-1">
                  {t('countryConfigurations.quantity.fields.defaultNumberOfTrucks')}
                </TextMedium>
                {errors.defaultNumberOfTrucks && !isReadOnly && (
                  <span id="quantity-default-trucks-error" className="text-xs leading-4 text-negative">
                    {t(errors.defaultNumberOfTrucks)}
                  </span>
                )}
              </div>
              <div className={FIELD_CONTROL}>
                <FilterDropdown
                  value={String(values.fullLoadOptions.defaultNumberOfTrucks)}
                  onChange={(v) =>
                    setField('fullLoadOptions.defaultNumberOfTrucks')(Number(v))
                  }
                  options={defaultTruckOptions}
                  label={t('countryConfigurations.quantity.fields.defaultNumberOfTrucks')}
                  size="medium"
                  aria-describedby={
                    errors.defaultNumberOfTrucks && !isReadOnly
                      ? 'quantity-default-trucks-error'
                      : undefined
                  }
                  data-cy="quantity-default-trucks"
                />
              </div>
            </div>
          </>
        )}
      </section>
      )}

      <hr className="border-border" />

      {/* --- Load Balance section --- */}
      {showLoadBalance && (
      <section className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <SectionTitle
            titleKey="countryConfigurations.quantity.sections.loadBalance.title"
            tooltipKey="countryConfigurations.quantity.sections.loadBalance.tooltip"
          />
          <div className="flex gap-0.5 items-center">
            {showDot && config.overrides.loadBalanceEnabled && <OverrideDot />}
            <CountryConfigsTooltip
              content={t('countryConfigurations.quantity.fields.loadBalanceEnabledTooltip')}
              delayMs={300}
            >
              <span className="text-foreground flex items-center cursor-default">
                <Icon path={mdiInformationOutline} size="regular" />
              </span>
            </CountryConfigsTooltip>
            <ToggleSwitch
              checked={values.loadBalanceEnabled}
              onChange={setField('loadBalanceEnabled')}
              disabled={isReadOnly}
              aria-label={t('countryConfigurations.quantity.sections.loadBalance.title')}
              data-cy="quantity-loadbalance-enabled"
            />
          </div>
        </div>

        {values.loadBalanceEnabled && (
          <>
            {renderNumericRow(
              'countryConfigurations.quantity.fields.minEligibleQuantity',
              values.minEligibleQuantity,
              setField('minEligibleQuantity'),
              'minEligibleQuantity',
              {suffix: values.uom, dataCy: 'quantity-loadbalance-min'},
            )}
            {renderNumericRow(
              'countryConfigurations.quantity.fields.maxEligibleQuantity',
              values.maxEligibleQuantity,
              setField('maxEligibleQuantity'),
              'maxEligibleQuantity',
              {suffix: values.uom, error: errors.maxEligibleQuantity, dataCy: 'quantity-loadbalance-max'},
            )}
          </>
        )}
      </section>
      )}
    </div>
  )
}
